import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  memo,
} from 'react';

import PropTypes from 'prop-types';

import CollapseColumnsBtn from '../../layout/CollapseColumnsBtn';
import Toolbar from '../../layout/Toolbar';
import CustomUI from '../CustomUI';
import MapCard from '../MapCard';
import Polygon from '../Polygon';
import Circle from '../Circle';
import Nearby from '../Nearby';
import LocalityPolygon from '../LocalityPolygon';
import ExpandLocalityRadius from '../ExpandLocalityRadius';
import ListLink from '../ListLink';
import YouGotAwayMsg from '../YouGotAwayMsg';
import ZoomMsg from '../ZoomMsg';
// import MarkerTypeDebug from '../MarkerTypeDebug';
import UndoDeleteShape from '../UndoDeleteShape';

import { useMapApiContext } from '../ApiLoader';
import useMapIdle from '../../../helpers/useMapIdle';

import { DRAWING_ACTIONS_MAP } from '../../../constants';

export const MapContext = createContext();
export const useMapContext = () => useContext(MapContext);

const boundsFallback = [{ bounds: [[48.809821242849445, 0.8928202195415906], [34.45592178596872, 23.15112100079159]] }];

const MapEl = ({
  center,
  zoom,
  bounds,
  markers,
  fakeLocalityMarkers,
  updatequery,
  setPinpoint,
  baseClassName,
  hasCustomUI = false,
  hasToolbar = false,
  searchMode,
  setSearchMode,
  mobileOs,
  device,
  viewport,
  activeMarkerData,
  setActiveMarker,
  activePoiMarkerData,
  setActivePoiMarker,
  fetchInfoboxData,
  setCurrentFloorplan,
  loadDetail,

  doShowOnMap,
  highlightedListCard,
  highlightedListCardClick,

  columnRef,
  setGeoFilters,
  setNearby,
  getAvailableResults,

  drawingData,
  setDrawingData,

  columnsAreCollapsed,

  totalResults,
  totalListResults,
  isDrawing,
  isFetching,
  listUrl,
  openQbFiltersPanel,
  qbGoToSRP,
  qbSSearchAction,
  controlsVariant,
  setMapData,
  setSaveSearchDrawingData,
  focusOnLocalityMarkers,
  eraseShape,
  resetSaveSearchConditions,
  isSafari,
  filters,
  artificialBackConfiguration,
  getDrawingData,
  synchPreviousStepsActiveShape,
  setInitialShapeForPrevious,
  gSearchOpen,
  toggleGSearchState,
  deleteDraftAndStopSearchMode,
  toggleDeleteDraftAndStopSearchMode,
  shapeAddressBaloon,
  setShapeAddressBaloon,
  mapPinpoint,
  hasCollapseColumns = true,
  hasListLink = true,
  hasYouGotAwayMsg = true,
  useInitialBounds = true,
  children,
}) => {
  const container = useRef(null);
  const searchComponent = useRef(null);

  const { ready, libraries } = useMapApiContext();

  const [mapEl, setMapEl] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showYouGotAwayMsg, setShowYouGotAwayMsg] = useState(false);

  const [boundsChangedOnBack, setBoundsChangedOnBack] = useState(false);
  const [shapeHasBeenDeleted, setShapeHasBeenDeleted] = useState(false);

  const { query } = useMapIdle({
    mapEl,
    searchMode,
    activeMarkerData,
    setActiveMarker,
    activePoiMarkerData,
    setActivePoiMarker,
    doShowOnMap,
    highlightedListCard,
    highlightedListCardClick,
    libraries,
    drawingData,
    shapeBounds: drawingData?.shapeBounds || null,
    showYouGotAwayMsg,
    setShowYouGotAwayMsg,
    focusOnLocalityMarkers,
    resetSaveSearchConditions,
    shapeAddressBaloon,
    setShapeAddressBaloon,
    shapeHasBeenDeleted,
    setShapeHasBeenDeleted,
    mapPinpoint,
    // the bounds set at init are the ones requested to fitBounds (markers bbox, polygon fit, ...): once the map is idle
    // the real viewport is known, and it is the position the first browser back step has to restore
    onFirstIdle: (map) => setMapData && setMapData({ center: map.getCenter(), zoom: map.getZoom(), bounds: map.getBounds() }),
  });

  useEffect(() => {
    if (ready && !initialized) {
      setInitialized(true);
      const {
        core: {
          LatLng,
          LatLngBounds,
        },
        maps: {
          Map,
          // Polyline,
        },
      } = libraries;

      console.log({ useInitialBounds });

      const usableBounds = useInitialBounds ? bounds || boundsFallback : null;

      const map = new Map(container.current, {
        center: center ? new LatLng(center.lat, center.lng) : null,
        zoom,
        disableDefaultUI: true,
        mapId: 'a1af761b14950b149ec8cd11',
        gestureHandling: 'greedy',
        clickableIcons: false,
      });

      let mapDataBounds;

      // se ci sono markers, ricaviamo il bounding box dalle loro coordinate
      if (markers.length) {
        const initialBounds = new LatLngBounds();
        // const p = new Polyline();

        markers.forEach(el => initialBounds.extend(new LatLng(el.lat, el.lon)));

        // var NE = bounds.getNorthEast();
        // var SW = bounds.getSouthWest();
        // // North West

        // console.log(NE.lat(), SW.lng());
        // console.log(SW.lat(), NE.lng());

        // var NW = new google.maps.LatLng(NE.lat(),SW.lng());
        // // South East
        // var SE = new google.maps.LatLng(SW.lat(),NE.lng());

        // p.setPath([NE, NW, SW, SE, NE]);
        // p.setMap(map);

        mapDataBounds = initialBounds;
        map.fitBounds(initialBounds, 0);

      // se ci sono località, usiamo il centro di ognuna di loro per ricavare un bounding box che le contenga tutte
      } else if (fakeLocalityMarkers.length) {
        const initialBounds = new window.google.maps.LatLngBounds();
        fakeLocalityMarkers.forEach(el => initialBounds.extend(new window.google.maps.LatLng(el.lat, el.lon)));

        const increasePercentage = device === 'desktop' ? 5 : 2;

        const ne = initialBounds.getNorthEast();
        const sw = initialBounds.getSouthWest();

        const latAdjustment = (ne.lat() - sw.lat()) * (increasePercentage - 1) || 0.01;
        const lngAdjustment = (ne.lng() - sw.lng()) * (increasePercentage - 1) || 0.01;

        const newne = new google.maps.LatLng(ne.lat() + latAdjustment, ne.lng() + lngAdjustment);
        const newsw = new google.maps.LatLng(sw.lat() - latAdjustment, sw.lng() - lngAdjustment);

        const newBounds = new google.maps.LatLngBounds();
        newBounds.extend(newne);
        newBounds.extend(newsw);

        mapDataBounds = newBounds;
        map.fitBounds(newBounds, 0);

      // infine, se ci arriva filters.geobounds lo usiamo. altrimenti facciamo una fallback sul bounding box che racchiude l'italia intera
      } else if (usableBounds) {                
        const computedBounds = new window.google.maps.LatLngBounds();
        // current sm version format: [{bounds:[...]}]; extract inner item if array
        const _b = Array.isArray(usableBounds) ? usableBounds[0] : usableBounds;
        const _bbox = _b.bounds || _b.bbox;
        const newne = new google.maps.LatLng(_bbox[0][0], _bbox[0][1]);
        const newsw = new google.maps.LatLng(_bbox[1][0], _bbox[1][1]);
        computedBounds.extend(newne);
        computedBounds.extend(newsw);
        mapDataBounds = computedBounds;
        map.fitBounds(computedBounds, 0);
      }

      if (setMapData) {
        const int = setInterval(() => {
          if (map.getCenter()) {
            setMapData({ center: map.getCenter(), zoom: map.getZoom(), bounds: mapDataBounds });
            clearInterval(int);
          }
        }, 25);
      }      
      setMapEl(map);
    }    
  }, [ready, libraries, center, zoom, bounds, markers]);

  useEffect(() => {
    if (focusOnLocalityMarkers && markers.length) {
      const {
        core: {
          LatLng,
          LatLngBounds,
        },
      } = libraries;
      const initialBounds = new LatLngBounds();
      markers.forEach(el => initialBounds.extend(new LatLng(el.lat, el.lon)));

      if (eraseShape && drawingData.activeShapes.length) {
        drawingData.activeShapes.map((s) => {
          if (s) {
            s.setMap(null);
          }          
        });
        setSearchMode(null);
        setDrawingData(null);
      }

      mapEl.fitBounds(initialBounds, 0);
      setMapData && setTimeout(() => setMapData({ center: mapEl.getCenter(), zoom: mapEl.getZoom(), bounds: initialBounds }), 10);
    }
  }, [focusOnLocalityMarkers, eraseShape, markers]);

  useEffect(() => {
    if (artificialBackConfiguration) {
      const { action } = artificialBackConfiguration;
      if (action === 'resetbounds') {
        boundsChangedOnBack && setBoundsChangedOnBack(false);
        mapEl.fitBounds(artificialBackConfiguration.bounds, 0);
      } else if (action === 'deleteShape' && artificialBackConfiguration.activeShapes.length) {
        artificialBackConfiguration.activeShapes.map((s) => {
          if (s) {
            s.setMap(null);
          }          
        });
        setSearchMode(null);
        setDrawingData(null);
        boundsChangedOnBack && setBoundsChangedOnBack(false);
        mapEl.fitBounds(artificialBackConfiguration.bounds, 0);
      } else if (action === 'revertToPreviousShape' && artificialBackConfiguration.activeShapes.length) {
        artificialBackConfiguration.activeShapes.map((s) => {
          if (s) {
            s.setMap(null);
          }          
        });
        setBoundsChangedOnBack(true);
        setDrawingData(null);
        setTimeout(() => setDrawingData(artificialBackConfiguration.previousShapeData));
      }
    }    
  }, [artificialBackConfiguration]);

  useEffect(() => {
    if (query && !searchMode) {
      if (!boundsChangedOnBack) {
        updatequery(query);
        setMapData && setMapData({ center: mapEl.getCenter(), zoom: mapEl.getZoom(), bounds: mapEl.getBounds() });
      } else {
        setBoundsChangedOnBack(false);
      }
    }
  }, [query]);

  const mapContextValue = useMemo(() => ({ map: mapEl }), [mapEl]);

  if (searchMode || drawingData) {
    let Component;
    const activeTool = searchMode || drawingData.action;

    switch (activeTool) {
      case DRAWING_ACTIONS_MAP.polygon:
        Component = Polygon;
        break;
      case DRAWING_ACTIONS_MAP.circle:
        Component = Circle;
        break;
      case DRAWING_ACTIONS_MAP.nearby:
        Component = Nearby;
        break;
      case DRAWING_ACTIONS_MAP.localityPolygons:
        Component = LocalityPolygon;
        break;
      case DRAWING_ACTIONS_MAP.expandLocalityRadius:
        Component = ExpandLocalityRadius;
        break;
      default:
    }

    searchComponent.current = Component ? <Component
      initialized={initialized}
      map={mapEl}
      container={container}
      searchMode={searchMode}
      setSearchMode={setSearchMode}
      device={device}
      mobileOs={mobileOs}
      libraries={libraries}
      setPinpoint={setPinpoint}
      setGeoFilters={setGeoFilters}
      setNearby={setNearby}
      getAvailableResults={getAvailableResults}
      drawingData={drawingData}
      setDrawingData={setDrawingData}
      updateQuery={updatequery}
      columnsAreCollapsed={columnsAreCollapsed}
      viewport={viewport}
      openQbFiltersPanel={openQbFiltersPanel}
      qbGoToSRP={qbGoToSRP}
      qbSSearchAction={qbSSearchAction}
      controlsVariant={controlsVariant}
      setSaveSearchDrawingData={setSaveSearchDrawingData}
      isSafari={isSafari}
      showYouGotAwayMsg={showYouGotAwayMsg}
      setShowYouGotAwayMsg={setShowYouGotAwayMsg}
      filters={filters}
      getDrawingData={getDrawingData}
      synchPreviousStepsActiveShape={synchPreviousStepsActiveShape}
      setBoundsChangedOnBack={setBoundsChangedOnBack}
      setInitialShapeForPrevious={setInitialShapeForPrevious}
      deleteDraftAndStopSearchMode={deleteDraftAndStopSearchMode}
      toggleDeleteDraftAndStopSearchMode={toggleDeleteDraftAndStopSearchMode}
      setShapeAddressBaloon={setShapeAddressBaloon}
      setShapeHasBeenDeleted={setShapeHasBeenDeleted}
      totalListResults={totalListResults}
      isFetching={isFetching}
    /> : null;
  } else if (!searchMode && !drawingData && searchComponent.current) {
    searchComponent.current = null;
  }
  
  return (
    <MapContext.Provider value={mapContextValue}>
      <>
        {children}
        <div className={`${baseClassName}__map is-rel`}>
          <div
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            {searchComponent.current}
            <div
              ref={container}
              style={{
                width: '100%',
                height: '100%',
                zIndex: 1,
              }}
            />
            {hasToolbar && (
              <Toolbar
                map={mapEl}
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                mobileOs={mobileOs}
                device={device}
                places={libraries?.places || null}
                core={libraries?.core || null}
                geocoding={libraries?.geocoding || null}
                setPinpoint={setPinpoint}
                isQBuilder={!!qbSSearchAction || !!qbGoToSRP}
                gSearchOpen={gSearchOpen}
                toggleGSearchState={toggleGSearchState}
              />
            )}
            {hasCustomUI && (
              <CustomUI
                map={mapEl}
                mobileOs={mobileOs}
                searchMode={searchMode}
                core={libraries?.core || null}
                geocoding={libraries?.geocoding || null}
                setPinpoint={setPinpoint}
                device={device}
              />
            )}
            {activeMarkerData && (
              <MapCard
                device={device}
                viewport={viewport}
                activeMarkerData={activeMarkerData}
                fetchInfoboxData={fetchInfoboxData}
                setActiveMarker={setActiveMarker}
                setCurrentFloorplan={setCurrentFloorplan}
                loadDetail={loadDetail}
              />
            )}
            {hasCollapseColumns && (
              <CollapseColumnsBtn
                columns={columnRef}
                unsetHighlightedListMarker={doShowOnMap}
                device={device}
              />              
            )}
            {hasListLink && (
              <ListLink
                isDrawing={isDrawing}
                totalListResults={totalListResults}
                device={device}
                isFetching={isFetching}
                listUrl={listUrl}
                drawingData={drawingData}
              />
            )}
            {hasYouGotAwayMsg && (
              <YouGotAwayMsg
                map={mapEl}
                drawingData={drawingData}
                setDrawingData={setDrawingData}
                updateQuery={updatequery}
                show={showYouGotAwayMsg}
                setShow={setShowYouGotAwayMsg}
                searchMode={searchMode}
              />
            )}
            {!qbGoToSRP ? (
              <ZoomMsg
                isDrawing={isDrawing || showYouGotAwayMsg}
                totalResults={totalResults}
                totalListResults={totalListResults}
                device={device}
              />
            ) : null}
            {/* <MarkerTypeDebug map={mapEl} markers={markers} />  */}
            {shapeHasBeenDeleted ? <UndoDeleteShape setShapeHasBeenDeleted={setShapeHasBeenDeleted} /> : null}
          </div>
        </div>
      </>       
    </MapContext.Provider>
  );
};

export default memo(MapEl);

MapEl.propTypes = {
  center: PropTypes.instanceOf(Object),
  zoom: PropTypes.number,
  markers: PropTypes.instanceOf(Array),
  fakeLocalityMarkers: PropTypes.instanceOf(Array),

  updatequery: PropTypes.func,
  setPinpoint: PropTypes.func,
  baseClassName: PropTypes.string,
  hasCustomUI: PropTypes.bool,
  hasToolbar: PropTypes.bool,
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  mobileOs: PropTypes.string,
  device: PropTypes.string,
  viewport: PropTypes.instanceOf(Object),
  activeMarkerData: PropTypes.instanceOf(Object),
  setActiveMarker: PropTypes.func,
  activePoiMarkerData: PropTypes.instanceOf(Object),
  setActivePoiMarker: PropTypes.func,
  fetchInfoboxData: PropTypes.func,
  setCurrentFloorplan: PropTypes.func,
  loadDetail: PropTypes.func,

  doShowOnMap: PropTypes.func,
  highlightedListCard: PropTypes.instanceOf(Object),
  highlightedListCardClick: PropTypes.bool,

  columnRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.string,
    PropTypes.node,
  ]),
  setGeoFilters: PropTypes.func,
  setNearby: PropTypes.func,
  getAvailableResults: PropTypes.func,

  drawingData: PropTypes.instanceOf(Object),
  setDrawingData: PropTypes.func,

  columnsAreCollapsed: PropTypes.bool,

  totalResults: PropTypes.number,
  totalListResults: PropTypes.number,
  isDrawing: PropTypes.bool,
  isFetching: PropTypes.bool,
  listUrl: PropTypes.string,
  openQbFiltersPanel: PropTypes.func,
  qbGoToSRP: PropTypes.func,
  qbSSearchAction: PropTypes.func,
  controlsVariant: PropTypes.string,
  bounds: PropTypes.instanceOf(Object),
  setMapData: PropTypes.func,
  setSaveSearchDrawingData: PropTypes.func,
  shapeAddressBaloon: PropTypes.instanceOf(Object), 
  setShapeAddressBaloon: PropTypes.func,
};

MapEl.defaultProps = {
  center: null,
  zoom: null,
  markers: [],
  fakeLocalityMarkers: [],

  updatequery: () => {},
  setPinpoint: () => {},
  baseClassName: '',
  hasCustomUI: false,
  hasToolbar: false,
  searchMode: null,
  setSearchMode: () => {},
  mobileOs: '',
  device: '',
  viewport: {},
  activeMarkerData: null,
  setActiveMarker: () => {},
  activePoiMarkerData: null,
  setActivePoiMarker: () => {},
  fetchInfoboxData: () => {},
  setCurrentFloorplan: () => {},
  loadDetail: () => {},

  doShowOnMap: () => {},
  highlightedListCard: {},
  highlightedListCardClick: false,

  columnRef: null,
  setGeoFilters: () => {},
  setNearby: () => {},
  getAvailableResults: () => {},

  drawingData: null,
  setDrawingData: () => {},

  columnsAreCollapsed: false,

  totalResults: 0,
  totalListResults: 0,
  isDrawing: false,
  isFetching: false,
  listUrl: '',
  openQbFiltersPanel: null,
  qbGoToSRP: null,
  qbSSearchAction: null,
  controlsVariant: null,
  bounds: null,
  setMapData: () => {},
  setSaveSearchDrawingData: () => {},
  shapeAddressBaloon: null,
  setShapeAddressBaloon: () => {},
};
